import { makeService } from '@upstart/shared-api-utils';
import { OkResponse } from 'util/api';
import { useTypedBackendClient } from './useTypedBackendClient';

export type ListDeletedDealersResponse = OkResponse<
  '/v2/workflow/dealer-setup/dealers/deleted',
  'get'
>['data'];

export type ReactivateDealerResponse = OkResponse<
  '/v2/workflow/dealer-setup/{dealerId}/reactivate',
  'put'
>['data'];

export interface DealerActivationService {
  listDeletedDealers(): Promise<ListDeletedDealersResponse>;
  reactivateDealer({
    dealerId,
  }: {
    dealerId: string;
  }): Promise<ReactivateDealerResponse>;
}

const useDealerActivationFns = (): DealerActivationService => {
  const { backendClient } = useTypedBackendClient();

  const listDeletedDealers: DealerActivationService['listDeletedDealers'] =
    async () => {
      const { data, error } = await backendClient.GET(
        '/v2/workflow/dealer-setup/dealers/deleted',
      );
      if (error) {
        throw new Error(error.detail || error.title);
      }
      return data.data;
    };

  const reactivateDealer: DealerActivationService['reactivateDealer'] = async ({
    dealerId,
  }) => {
    const { data, error } = await backendClient.PUT(
      '/v2/workflow/dealer-setup/{dealerId}/reactivate',
      {
        params: {
          path: { dealerId },
        },
      },
    );
    if (error) {
      throw new Error(error.detail || error.title);
    }
    return data.data;
  };

  return {
    listDeletedDealers,
    reactivateDealer,
  };
};

const {
  OverridesProvider: DealerActivationServiceOverridesProvider,
  useService: useDealerActivationService,
} = makeService({
  name: 'DealerActivation',
  useServiceFns: useDealerActivationFns,
});

export { DealerActivationServiceOverridesProvider, useDealerActivationService };
